import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import {
  History,
  Calendar,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
} from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";
import { formatDateTime } from "../lib/utils";

const ATELIER_LABELS = {
  1: "Atelier 1 - Compoziție corporală și forță",
  2: "Atelier 2 - Evaluare cardiovasculară",
  3: "Atelier 3",
  4: "Atelier 4 - Examen clinic pe sisteme",
};

const ATELIER_COLORS = {
  1: "bg-blue-100 text-blue-800 border-blue-200",
  2: "bg-emerald-100 text-emerald-800 border-emerald-200",
  3: "bg-amber-100 text-amber-800 border-amber-200",
  4: "bg-purple-100 text-purple-800 border-purple-200",
};

// Iconiță colorată în funcție de status
const StatusIcon = ({ status, className = "w-4 h-4" }) => {
  if (["optim", "excelent", "foarte_bun", "bun", "bine", "foarte_bine"].includes(status)) {
    return <CheckCircle className={`${className} text-emerald-600`} />;
  }
  if (["potential", "mediu", "mediocru"].includes(status)) {
    return <AlertCircle className={`${className} text-yellow-600`} />;
  }
  if (["nesatisfacator"].includes(status)) {
    return <AlertTriangle className={`${className} text-orange-600`} />;
  }
  if (["slab", "foarte_slab"].includes(status)) {
    return <XCircle className={`${className} text-red-600`} />;
  }
  return null;
};

// Badge-uri rezumat pe atelier
const SummaryBadges = ({ atelierNum, summary }) => {
  if (!summary) return null;
  const badges = [];

  if (atelierNum === 1) {
    if (summary.imc) {
      badges.push(
        <Badge key="imc" variant="secondary" className="gap-1">
          IMC: {summary.imc} {summary.imc_interpretare && `· ${summary.imc_interpretare}`}
        </Badge>
      );
    }
    if (summary.ta_procent) {
      badges.push(
        <Badge key="ta" variant="secondary">TA%: {summary.ta_procent}</Badge>
      );
    }
    if (summary.i_fma_interpretare) {
      badges.push(
        <Badge key="ifma" variant="outline" className="gap-1">
          <StatusIcon status={summary.i_fma_status} />
          {summary.i_fma_interpretare}
        </Badge>
      );
    }
  }

  if (atelierNum === 2) {
    if (summary.ruffier_rezultat !== undefined) {
      badges.push(
        <Badge key="ruff" variant="outline" className="gap-1">
          <StatusIcon status={summary.ruffier_status} />
          Ruffier: {summary.ruffier_rezultat} ({summary.ruffier_interpretare})
        </Badge>
      );
    }
    if (summary.vo2_relativ) {
      badges.push(
        <Badge key="vo2" variant="outline" className="gap-1">
          <StatusIcon status={summary.astrand_interpretare_status} />
          VO₂ rel: {summary.vo2_relativ} ({summary.astrand_interpretare})
        </Badge>
      );
    }
    if (summary.stt_rezultat) {
      badges.push(
        <Badge key="stt" variant="outline" className="gap-1">
          <StatusIcon status={summary.stt_calificativ_status} />
          STT: {summary.stt_rezultat} ({summary.stt_calificativ})
        </Badge>
      );
    }
  }

  if (atelierNum === 4) {
    const mods = summary.sisteme_modificate || [];
    if (mods.length === 0) {
      badges.push(
        <Badge key="allnorm" className="gap-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
          <CheckCircle className="w-3 h-3" /> Toate sistemele Normal
        </Badge>
      );
    } else {
      badges.push(
        <Badge key="mods" variant="destructive" className="gap-1">
          {mods.length} sistem{mods.length > 1 ? "e" : ""} modificat{mods.length > 1 ? "e" : ""}
        </Badge>
      );
    }
    if (summary.tesut_status && summary.tesut_status !== "normoponderal") {
      badges.push(
        <Badge key="tesut" variant="outline" className="capitalize">
          Țesut: {summary.tesut_status}
        </Badge>
      );
    }
  }

  return <div className="flex flex-wrap gap-1.5 mt-2">{badges}</div>;
};

// Formatare key-value pentru snapshot view
const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Da" : "Nu";
  if (typeof v === "number") return v.toFixed ? (Number.isInteger(v) ? v : v.toFixed(2)) : v;
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Formatări etichete câmpuri
const FIELD_LABELS = {
  talie: "Talie (m)", greutate: "Greutate (kg)", bust: "Bust (cm)", anvergura: "Anvergura (cm)",
  mobilitate: "Mobilitate", pl_abdomen: "Pl. Abdomen", pl_flanc: "Pl. Flanc",
  pl_spate: "Pl. Spate", pl_brat: "Pl. Braț", pl_coapsa: "Pl. Coapsă",
  f_flexori_dr: "F. Flexori Dr", f_flexori_stg: "F. Flexori Stg",
  f_scapulara: "F. Scapulară", f_lombara: "F. Lombară",
  imc: "I.M.C.", imc_interpretare: "Interpretare IMC",
  ta_procent: "TA%", ma_procent: "MA%", ta_kg: "TA (kg)", ma_kg: "MA (kg)",
  greutate_optima: "Greutate optimă", recomandare_sport: "Recomandare sport",
  i_flexori: "I.Flexori", i_scapulara: "I.Scapulară", i_lombara: "I.Lombară",
  i_fg: "I.F.G", i_fma: "I.F.M.A", i_fma_interpretare: "Interpretare I.F.M.A",
  av_ortostatism: "AV ortostatism", ta_ortostatism: "TA ortostatism",
  av_clinostatism: "AV clinostatism", ta_clinostatism: "TA clinostatism",
  ruffier_p1: "Ruffier P1", ruffier_p2: "Ruffier P2", ruffier_p3: "Ruffier P3",
  ruffier_rezultat: "Ruffier rezultat", ruffier_interpretare: "Ruffier interpretare",
  astrand_p2: "Astrand P 2'", astrand_p4: "Astrand P 4'", astrand_p6: "Astrand P 6'",
  astrand_p9: "Astrand P 9'", astrand_p12: "Astrand P 12'",
  astrand_ta6: "Astrand TA 6'", astrand_ta9: "Astrand TA 9'", astrand_ta12: "Astrand TA 12'",
  astrand_vo2_max: "VO₂ max", astrand_vo2_relativ: "VO₂ relativ",
  astrand_vo2_relativ_corectat: "VO₂ relativ corectat",
  astrand_interpretare: "Astrand interpretare", astrand_conformitate: "Conformitate",
  astrand_revenire: "Evaluare revenire", astrand_revenire_recomandare: "Recomandare revenire",
  stt_rezultat: "STT rezultat", stt_calificativ: "STT calificativ",
  observatii: "Observații",
};

const SKIP_FIELDS = new Set([
  "completat", "data_completare",
  "i_flexori_valid", "i_scapulara_valid", "i_lombara_valid",
  "i_fma_status", "ruffier_status", "ruffier_rezultat_brut", "ruffier_multiplicator",
  "astrand_activat", "ruffier_activat", "stt_activat",
  "astrand_factor_corectie", "astrand_interpretare_status", "astrand_conform",
  "astrand_revenire_status", "stt_calificativ_status",
]);

// Snapshot viewer - formatat simplu pentru read-only
const SnapshotView = ({ atelierNum, snapshot }) => {
  if (!snapshot) return null;

  // Pentru Atelier 4 - afișare dedicată pe sisteme
  if (atelierNum === 4) {
    const sisteme = [
      { key: "tegumente", label: "Tegumente și mucoase" },
      { key: "ganglionar", label: "Sistem ganglionar" },
      { key: "locomotor", label: "Aparat locomotor" },
      { key: "respirator", label: "Aparat respirator" },
      { key: "cardiovascular", label: "Aparat cardiovascular" },
      { key: "digestiv", label: "Aparat digestiv" },
      { key: "urogenital", label: "Aparat urogenital" },
      { key: "endocrin", label: "Sistem endocrin" },
    ];
    return (
      <div className="space-y-2">
        <div className="p-3 bg-slate-50 rounded border">
          <p className="text-sm font-medium">Țesut celular subcutanat</p>
          <p className="text-sm text-slate-700">{snapshot.tesut_descriere || "—"}</p>
        </div>
        {sisteme.map((s) => {
          const st = snapshot[`${s.key}_status`];
          const desc = snapshot[`${s.key}_descriere`];
          const isModif = st === "Modificat";
          return (
            <div
              key={s.key}
              className={`p-3 rounded border ${isModif ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                {isModif ? (
                  <XCircle className="w-4 h-4 text-red-500" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                )}
                <p className="text-sm font-medium">{s.label}</p>
                <Badge variant={isModif ? "destructive" : "secondary"} className="text-xs ml-auto">
                  {st}
                </Badge>
              </div>
              {isModif && desc && <p className="text-sm text-slate-700 italic">{desc}</p>}
            </div>
          );
        })}
        {snapshot.observatii && (
          <div className="p-3 bg-slate-50 rounded border">
            <p className="text-sm font-medium">Observații</p>
            <p className="text-sm">{snapshot.observatii}</p>
          </div>
        )}
      </div>
    );
  }

  // Generic: grid de câmpuri
  const entries = Object.entries(snapshot).filter(
    ([k, v]) =>
      !SKIP_FIELDS.has(k) &&
      v !== "" &&
      v !== null &&
      v !== 0 &&
      v !== false &&
      v !== undefined
  );

  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground italic">Fișa nu conține date.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
      {entries.map(([k, v]) => (
        <div key={k} className="text-sm flex justify-between gap-2 border-b pb-1">
          <span className="text-muted-foreground">{FIELD_LABELS[k] || k}:</span>
          <span className="font-medium text-right">{formatValue(v)}</span>
        </div>
      ))}
    </div>
  );
};

export const IstoricSportiv = ({ patientId, istoric, onChange }) => {
  const [viewing, setViewing] = useState(null);

  const handleDelete = async (entryId) => {
    try {
      await patientApi.deleteIstoricEntry(patientId, entryId);
      toast.success("Intrarea a fost ștearsă din istoric");
      if (onChange) onChange(istoric.filter((e) => e.id !== entryId));
    } catch (error) {
      console.error(error);
      toast.error("Eroare la ștergere");
    }
  };

  const sorted = [...(istoric || [])].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            Istoric sportiv
            <Badge variant="secondary" className="ml-1 text-xs">
              {sorted.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sorted.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <p>Nu există fișe arhivate.</p>
              <p className="text-xs mt-1">
                Fișele se arhivează automat când marchezi un Atelier ca fiind
                <strong> completat</strong> și salvezi.
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="istoric-list">
              {sorted.map((entry) => (
                <div
                  key={entry.id}
                  className={`rounded-lg border p-3 transition hover:shadow-sm ${ATELIER_COLORS[entry.atelier_num] || "bg-slate-50"}`}
                  data-testid={`istoric-entry-${entry.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="bg-white">
                          {ATELIER_LABELS[entry.atelier_num] || `Atelier ${entry.atelier_num}`}
                        </Badge>
                        <span className="text-xs flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDateTime(entry.data_completare || entry.date)}
                        </span>
                      </div>
                      <SummaryBadges atelierNum={entry.atelier_num} summary={entry.summary} />
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 bg-white/70"
                        onClick={() => setViewing(entry)}
                        data-testid={`istoric-view-${entry.id}`}
                        title="Vezi fișa"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 bg-white/70 text-red-600 hover:text-red-700"
                            title="Șterge din istoric"
                            data-testid={`istoric-delete-${entry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Șterge fișa din istoric?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Această acțiune nu poate fi anulată.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Anulează</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(entry.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Șterge
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Read-only viewer */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="istoric-viewer-dialog"
        >
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-primary" />
                  {ATELIER_LABELS[viewing.atelier_num]}
                </DialogTitle>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Completat la: {formatDateTime(viewing.data_completare || viewing.date)}
                </p>
              </DialogHeader>
              <div className="mt-3">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
                  🔒 Vizualizare read-only (fișă arhivată). Pentru modificări, completează o fișă nouă.
                </p>
                <SnapshotView atelierNum={viewing.atelier_num} snapshot={viewing.snapshot} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
